import { useEffect, useState, useRef, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, CalendarDays, MapPin, DollarSign, Info, Map,
  Ticket, X, Image, Luggage, Utensils, MoreHorizontal, Upload, Users, Icon, BookImage,
} from '../../components/ui/Icon';
import ImportDocumentModal from '../../components/trips/ImportDocumentModal';
import ParticipantManager from '../../components/trips/ParticipantManager';
import ShareLinkPanel from '../../components/trips/share/ShareLinkPanel';
import TripContentLanguage from '../../components/trips/TripContentLanguage';
import Modal from '../../components/ui/Modal';
import { useTripNotifications } from '../../hooks/useTripNotifications';
import { useItineraryHistory, type ItineraryHistory } from '../../hooks/useItineraryHistory';
import { tripsApi } from '../../api/trips.api';
import { itineraryApi } from '../../api/itinerary.api';
import type { Trip, ItineraryDay } from '../../types';
import LoadingScreen from '../../components/ui/LoadingScreen';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { toast } from '../../components/ui/Toast';
import TripSidebar from '../../components/trips/TripSidebar';
import EditableTripName from '../../components/trips/EditableTripName';
import TripTour from '../../components/trips/TripTour';
import { AiPanelProvider } from '../../components/ai/AiPanelContext';
import AiDockPanel from '../../components/ai/AiDockPanel';
import { tripMemberCount } from '../../utils/tripMembers';
import { hidesTab, tripPermissions } from '../../utils/tripPermissions';

// ── Context ──────────────────────────────────────────────────────────────────
interface TripCtx {
  trip: Trip;
  reload: () => void;
  days: ItineraryDay[];
  setDays: React.Dispatch<React.SetStateAction<ItineraryDay[]>>;
  loadDays: () => Promise<void>;
  /** Undo/redo for the plan, shared by the Itinerary tab and the map's routes (ADR-0015). */
  history: ItineraryHistory;
}
export const TripContext = createContext<TripCtx | null>(null);
export function useTripContext() {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTripContext outside TripDetailPage');
  return ctx;
}

// ── Mobile nav items ──────────────────────────────────────────────────────────
function useMobileNav() {
  const { t } = useTranslation();
  return [
    { to: 'itinerary', shortLabel: t('trip.nav.plan'),        NavIcon: CalendarDays },
    { to: 'venues',    shortLabel: t('trip.nav.placesShort'), NavIcon: MapPin       },
    { to: 'map',       shortLabel: t('trip.nav.mapShort'),    NavIcon: Map          },
    { to: 'bookings',  shortLabel: t('trip.nav.docsShort'),   NavIcon: Ticket       },
    { to: 'expenses',  shortLabel: t('trip.nav.money'),       NavIcon: DollarSign   },
    { to: 'meals',     shortLabel: t('trip.nav.mealsShort'),  NavIcon: Utensils     },
    { to: 'packing',   shortLabel: t('trip.nav.packingShort'), NavIcon: Luggage     },
    { to: 'info',      shortLabel: t('trip.nav.infoShort'),   NavIcon: Info         },
    { to: 'notes',     shortLabel: t('trip.nav.notesShort'),    NavIcon: BookOpen    },
    { to: 'gallery',   shortLabel: t('trip.nav.galleryShort'),  NavIcon: Image       },
    { to: 'album',     shortLabel: t('trip.nav.album'),         NavIcon: BookImage   },
  ];
}

export default function TripDetailPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const { t } = useTranslation();
  const [trip, setTrip]   = useState<Trip | null>(null);
  const [days, setDays]   = useState<ItineraryDay[]>([]);
  const history = useItineraryHistory(tripId ?? '', days, setDays);
  const [loading, setLoading] = useState(true);
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);
  const [confirmDemoDelete, setConfirmDemoDelete] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const tourAutoStarted = useRef(false);
  const MOBILE_NAV = useMobileNav().filter(item => !trip || !hidesTab(trip, item.to));
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Bottom nav: 5 primary tabs + a "more" popover for the rest
  const PRIMARY = MOBILE_NAV.slice(0, 5);
  const OVERFLOW = MOBILE_NAV.slice(5);
  const overflowActive = OVERFLOW.some(item => location.pathname.endsWith(`/${item.to}`));

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // The demo trip is the guided onboarding, so the tour auto-opens the first
  // time it's entered. Once the user has seen it (skip/finish/close) we remember
  // it in localStorage per trip so we don't reopen it on later visits — the
  // banner's "start" button still relaunches it on demand.
  const tourSeenKey = trip ? `gromly.tourSeen.${trip.id}` : null;
  useEffect(() => {
    if (!trip?.isDemo || tourAutoStarted.current) return;
    tourAutoStarted.current = true;
    if (tourSeenKey && localStorage.getItem(tourSeenKey)) return;
    setTourOpen(true);
  }, [trip?.isDemo, tourSeenKey]);

  function closeTour() {
    setTourOpen(false);
    if (tourSeenKey) localStorage.setItem(tourSeenKey, '1');
  }
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Navigating between trips reuses this page, so a response can land after the
  // URL already points at another trip. Dropping those matters beyond a flicker:
  // `trip` and `days` would describe DIFFERENT trips (they load independently),
  // and every nested call built from that pair (tripId from one, dayId/activityId
  // from the other) is exactly what `requireTripResource` rejects with a 404.
  const currentTripId = useRef(tripId);
  currentTripId.current = tripId;

  async function loadDays() {
    const requested = tripId!;
    try {
      const d = await itineraryApi.listDays(requested);
      if (requested === currentTripId.current) setDays(d);
    } catch {
      // non-fatal
    }
  }

  async function loadTrip() {
    const requested = tripId!;
    try {
      const loadedTrip = await tripsApi.get(requested);
      if (requested === currentTripId.current) setTrip(loadedTrip);
    } catch {
      if (requested !== currentTripId.current) return;
      toast(t('trip.notFound'), 'error');
      navigate('/dashboard');
    } finally {
      if (requested === currentTripId.current) setLoading(false);
    }
  }

  // Clearing on tripId change is what keeps the tabs from rendering the previous
  // trip's days under the new trip's id while the fetches are in flight.
  useEffect(() => {
    setLoading(true);
    setTrip(null);
    setDays([]);
    loadTrip();
    if (tripId) loadDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const notifications = useTripNotifications(trip ?? ({ flights: [], hotels: [] } as unknown as Trip));

  if (loading) {
    return <LoadingScreen />;
  }
  if (!trip) return null;

  // Sharing the trip outside it is the owner's call, and the endpoints behind it
  // require OWNER anyway — showing the panel to an editor would only offer a
  // button that 403s.
  const { isOwner, canEdit } = tripPermissions(trip);

  return (
    <TripContext.Provider value={{ trip, reload: loadTrip, days, setDays, loadDays, history }}>
      <AiPanelProvider>
      <div className="flex h-full relative">
        {/* ── Trip sidebar ────────────────────────────────────── */}
        <TripSidebar
          trip={trip}
          onTripUpdate={t => setTrip(t)}
          onDaysChanged={setDays}
          canEdit={canEdit}
          notifications={notifications}
          onManageParticipants={() => setParticipantsOpen(true)}
        />

        {/* ── Tab content ─────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden bg-slate-50 dark:bg-slate-950 flex flex-col min-w-0">
          {/* Mobile trip header bar */}
          <div className="md:hidden flex items-center gap-2 px-4 py-1.5 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-7 h-7 -ml-1 rounded-lg flex items-center justify-center shrink-0 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={t('common.back')}
            >
              <Icon as={ArrowLeft} size={18} />
            </button>
            {/* The sidebar is desktop-only, so this is where a phone renames the trip. */}
            <div className="flex-1 min-w-0">
              <EditableTripName
                trip={trip}
                canEdit={canEdit}
                onRenamed={setTrip}
                className="font-semibold text-slate-900 dark:text-slate-100 text-sm"
                displayClassName="truncate"
              />
            </div>
            <button
              onClick={() => setParticipantsOpen(true)}
              className="h-7 px-1.5 rounded-lg flex items-center gap-1 shrink-0 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={t('trip.participants.title')}
            >
              <Icon as={Users} size={18} />
              <span className="text-[11px] font-semibold tabular-nums">{tripMemberCount(trip)}</span>
            </button>
            <button
              data-tour="import-doc"
              onClick={() => setImportOpen(true)}
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={t('import.sidebarButton')}
            >
              <Icon as={Upload} size={18} />
            </button>
          </div>
          {/* Demo pill */}
          {trip.isDemo && !demoBannerDismissed && (
            <div className="shrink-0 flex items-center justify-between px-4 py-2 bg-amber-400/10 dark:bg-amber-400/5 border-b border-amber-200/60 dark:border-amber-800/40">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-wide">Demo</span>
                <span className="text-xs text-amber-700 dark:text-amber-400">{t('trip.demo.banner')}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => setTourOpen(true)}
                  className="text-xs font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-colors cursor-pointer"
                >
                  {t('trip.tour.start')}
                </button>
                <button
                  onClick={() => setConfirmDemoDelete(true)}
                  className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors cursor-pointer"
                >
                  {t('common.delete')}
                </button>
                <button
                  onClick={() => setDemoBannerDismissed(true)}
                  className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 transition-colors cursor-pointer"
                >
                  <Icon as={X} size={14} />
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto min-h-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
            {/* ADR-0038: a bookmarked /expenses must not land staff on a 403. */}
            {trip && hidesTab(trip, location.pathname.split('/').pop() ?? '') ? <Navigate to="itinerary" replace /> : <Outlet />}
          </div>
        </div>

        {/* ── AI assistant (docked rail / drawer) ─────────────── */}
        <AiDockPanel trip={trip} />
      </div>
      </AiPanelProvider>

      {/* ── Mobile bottom nav ──────────────────────────────────── */}
      {/* The More menu lives inside this bar's stacking context, so it can only
          rise above what the bar rises above. The map's stops sheet and the gIA
          button are z-40 and covered it; z-[45] while it's open clears them and
          still stays under a modal (z-50). */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 ${moreOpen ? 'z-[45]' : 'z-30'} bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex pb-[env(safe-area-inset-bottom)]`}>
        {PRIMARY.map(({ to, shortLabel, NavIcon }) => (
          <NavLink
            key={to}
            to={to}
            data-tour={`nav-${to}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors relative ${
                isActive
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400'
              }`
            }
          >
            <div className="relative">
              <Icon as={NavIcon} size={20} />
              {to === 'bookings' && notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full text-white text-[8px] font-bold flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </div>
            <span className="text-[9px] font-semibold">{shortLabel}</span>
          </NavLink>
        ))}

        {/* More — opens the remaining tabs upward */}
        <div className="flex-1 relative" ref={moreRef}>
          <button
            onClick={() => setMoreOpen(o => !o)}
            className={`w-full flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
              overflowActive || moreOpen
                ? 'text-teal-600 dark:text-teal-400'
                : 'text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400'
            }`}
            aria-label={t('trip.nav.more')}
            aria-expanded={moreOpen}
          >
            <Icon as={MoreHorizontal} size={20} />
            <span className="text-[9px] font-semibold">{t('trip.nav.more')}</span>
          </button>
          {moreOpen && (
            <div className="absolute bottom-full right-2 mb-2 w-52 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden py-1.5 animate-slide-up">
              {OVERFLOW.map(({ to, shortLabel, NavIcon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`
                  }
                >
                  <Icon as={NavIcon} size={18} />
                  {shortLabel}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

    {tourOpen && <TripTour tripId={tripId!} trip={trip} onClose={closeTour} />}

    <ConfirmModal
      open={confirmDemoDelete}
      title={t('trip.demo.deleteConfirm')}
      onConfirm={async () => {
        setConfirmDemoDelete(false);
        try {
          await tripsApi.delete(tripId!);
          navigate('/dashboard');
        } catch {
          toast(t('trip.demo.deleteFailed'), 'error');
        }
      }}
      onCancel={() => setConfirmDemoDelete(false)}
    />

    <Modal open={participantsOpen} onClose={() => setParticipantsOpen(false)} title={t('trip.manage.title')}>
      <ParticipantManager trip={trip} onUpdate={loadTrip} />
      {isOwner && <ShareLinkPanel trip={trip} onTripUpdate={setTrip} />}
      <TripContentLanguage trip={trip} canEdit={canEdit} onSaved={loadTrip} />
    </Modal>

    {importOpen && (
      <ImportDocumentModal
        tripId={trip.id}
        onClose={() => setImportOpen(false)}
        onImported={() => { loadTrip(); loadDays(); }}
      />
    )}
    </TripContext.Provider>
  );
}
